/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import { Snackbar } from '@mui/material';
import awsConfig from '../../../../Configuration/environment-config';
import { signedFetch } from '../../../../Shared/Utilities/utils';

const getCognitoAccessToken = () => {
  const localStorageKeys = Object.keys(localStorage);
  const accessTokenKey = localStorageKeys.find(
    (key) => key.includes('CognitoIdentityServiceProvider') && key.endsWith('accessToken')
  );
  return accessTokenKey ? localStorage.getItem(accessTokenKey) : null;
};

const VMSLogin = () => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation(['Dashboard', 'tableHeader', 'homePage', 'altTexts', 'calendar']);

  useEffect(() => {
    handleVmsLogin();
  }, []); // Runs once when the component mounts

  const handleVmsLogin = async () => {
    try {
      setSnackbar({ open: true, message: t('Redirecting...'), severity: 'success' });
      await VMSViewApiService.vmsLogin();
    } catch (error) {
      console.error('Error during VMS login', error);
      setSnackbar({ open: true, message: t('Login failed'), severity: 'error' });
    }
  };

  return (
    <>
      <PageTitle title={t('dashboard:vms-login')} />
      {snackbar.open && (
        <Snackbar
          open={snackbar.open}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      )}
    </>
  );
};

export default VMSLogin;

class VMSViewApiService {
  static async vmsLogin() {
    try {
      const cognitoAccessToken = getCognitoAccessToken();
      if (!cognitoAccessToken) {
        console.error('Cognito token not found');
        return;
      }

      const mintEndpoint = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'vmsHandoffMint');
      const vmsLoginEndpoint = awsConfig.aws_cloud_logic_custom.find((api) => api.name === 'vmsLogin');
      if (!mintEndpoint || !vmsLoginEndpoint) {
        console.error('VMS hand-off endpoints not configured');
        return;
      }

      // Exchange the Cognito session for a single-use, short-lived hand-off code.
      // Nothing sensitive travels in the redirect URL — only the opaque code.
      // The mint endpoint sits behind the AWS_IAM gateway, so the request is
      // SigV4-signed like every other usermanagement call. The Cognito access
      // token travels in the signed request body (not a custom header) so the
      // request carries no header outside the gateway's CORS allow-list.
      const mintResult = await signedFetch({
        method: 'POST',
        url: mintEndpoint.endpoint,
        body: { accessToken: cognitoAccessToken },
      });

      if (mintResult.status !== 200 || !mintResult.data?.code) {
        console.error('Failed to mint VMS hand-off code', mintResult.status, mintResult.errors);
        return;
      }

      const { code } = mintResult.data;

      window.location.href = `${vmsLoginEndpoint.endpoint}?code=${encodeURIComponent(code)}`;
    } catch (error) {
      console.error('Redirection failed', error);
    }
  }
}
